import { TextField, Typography, useTheme } from "@mui/material";
import { ChangeEventHandler, PropsWithChildren, type JSX } from 'react';
import { Controller, useFormContext } from "react-hook-form";
import { FormFieldPropsBase } from "../form/Form";

export type FormSelectProps = {
    rules?: any;
    size?: "small" | "medium" | undefined;
    boldOnDirty?: boolean;
    startAdornment?: JSX.Element;
    onChanging?: (toValue: string | number, fromValue: string | number) => Promise<boolean>;
    onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
} & FormFieldPropsBase;

export default function FormSelect({
    name,
    id,
    testId,
    label,
    ariaLabel,
    readOnly,
    helperText,
    rules,
    children,
    sx,
    disabled,
    size,
    boldOnDirty,
    startAdornment,
    onChanging,
    onChange,
    showID
}: PropsWithChildren<FormSelectProps>): JSX.Element {
    const { control } = useFormContext();
    const theme = useTheme();

    return (
        <Controller
                control={control}
                name={name}
                rules={rules}
                render={
                    ({
                        field: { ref, name, value, onChange: onFieldChange, onBlur },
                        fieldState: { invalid, isTouched, isDirty, error },
                        formState
                    }: {
                        field: any;
                        fieldState: any;
                        formState: any;
                    }) => (
                        <TextField
                            variant="filled"
                            select
                            id={id}
                            data-testid={testId}
                            aria-label={ariaLabel ?? undefined}
                            sx={{
                                marginTop: 0,
                                ...sx
                            }}
                            label={label ?? undefined}
                            error={invalid}
                            helperText={invalid ? error.message : helperText}
                            onBlur={onBlur}
                            onChange={
                                async (e) => {
                                    if (!onChanging || (await onChanging(e.target.value, value))) {
                                        onFieldChange(e);
                                        onChange && onChange(e);
                                    }
                                }
                            }
                            inputRef={ref}
                            // it appears that the value must be a string for this thing to work right
                            // even when the option values are numbers
                            value={`${value}`}
                            disabled={disabled}
                            size={size}
                            slotProps={{
                                input: {
                                    readOnly: readOnly,
                                    sx: {
                                        fontWeight: boldOnDirty && isDirty ? 700 : 400
                                    },
                                    startAdornment: startAdornment,
                                    endAdornment: showID ? <Typography sx={{
                                                    color: theme.palette.primary.light,
                                                    position: 'absolute', 
                                                    fontSize: 'small',
                                                    top: '6px', 
                                                    right: '12px'
                                                }}>{`[${id}]`}
                                                </Typography> : null

                                },

                                inputLabel: {
                                    shrink: true,
                                    sx: {
                                        fontWeight: boldOnDirty && isDirty ? 700 : 400
                                    }
                                }
                            }}>
                            {children}
                        </TextField>
                    )
                }
            />
    );
}